import { useState, forwardRef, useImperativeHandle } from 'react';
import { Modal, Form, ButtonGroup, ToggleButton } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDonate } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import LoadingButton from '@/components/ui/LoadingButton';

const DonateModal = forwardRef((props, ref) => {
  const { creatorName, onConfirm } = props;
  const { t } = useTranslation();

  const amountOptions = [
    { value: '1', label: '₣1' },
    { value: '5', label: '₣5' },
    { value: '10', label: '₣10' },
    { value: '20', label: '₣20' },
    { value: 'custom', label: t('custom_amount') }
  ];

  const [show, setShow] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState(amountOptions[0].value);
  const [customAmount, setCustomAmount] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useImperativeHandle(ref, () => ({
    show: () => {
      setSelectedAmount(amountOptions[0].value);
      setCustomAmount('');
      setMessage('');
      setShow(true);
    }
  }));

  const handleClose = () => {
    setShow(false);
    setSelectedAmount(amountOptions[0].value);
    setCustomAmount('');
    setMessage('');
  };

  // 打賞會扣款，送出後鎖住 modal 直到 onConfirm 完成，避免重複送出
  const handleConfirm = async () => {
    const amount = selectedAmount === 'custom' ? customAmount : selectedAmount;
    setIsSubmitting(true);
    try {
      await onConfirm(amount, message);
    } finally {
      setIsSubmitting(false);
      handleClose();
    }
  };

  const handleAmountChange = (value) => {
    setSelectedAmount(value);
    if (value !== 'custom') {
      setCustomAmount('');
    }
  };

  const isConfirmDisabled = () => {
    // 檢查留言是否為空
    if (!message.trim()) {
      return true;
    }
    // 檢查金額
    if (selectedAmount === 'custom') {
      return !customAmount || isNaN(customAmount) || Number(customAmount) <= 0;
    }
    return false;
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      backdrop={isSubmitting ? 'static' : true}
      keyboard={!isSubmitting}
    >
      <Modal.Header className="border-bottom pb-3" closeButton={!isSubmitting}>
        <Modal.Title className="fw-bold">{t('donate')}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* 創作者資訊 */}
        <div className="mb-4 d-flex align-items-center gap-2">
          <FontAwesomeIcon icon={faDonate} className="text-primary fs-4 flex-shrink-0" />
          <div className="min-w-0 break-anywhere">
            <span>{t('donate_support_creator')}<span className="fw-bold ms-1">{creatorName}</span></span>
          </div>
        </div>

        {/* 打賞金額 */}
        <div className="mb-4">
          <Form.Label className="fw-bold">{t('donate_amount_label')}</Form.Label>
          <div className="d-flex flex-column gap-2 w-100">
            <ButtonGroup className="w-100">
              {amountOptions.map((option) => (
                <ToggleButton
                  key={option.value}
                  id={`amount_${option.value}`}
                  type="radio"
                  variant={selectedAmount === option.value ? 'primary' : 'outline-primary'}
                  name="amount"
                  value={option.value}
                  checked={selectedAmount === option.value}
                  onChange={() => handleAmountChange(option.value)}
                  className="px-0"
                  disabled={isSubmitting}
                >
                  {option.label}
                </ToggleButton>
              ))}
            </ButtonGroup>
            {selectedAmount === 'custom' && (
              <Form.Control
                type="number"
                placeholder="FCoin"
                value={customAmount}
                onChange={(e) => {
                  // 最多兩位小數：後端會把超過的位數四捨五入，擋在輸入端避免實扣金額與輸入不同
                  if (/^\d*(\.\d{0,2})?$/.test(e.target.value)) {
                    setCustomAmount(e.target.value);
                  }
                }}
                min="1"
                disabled={isSubmitting}
              />
            )}
          </div>
        </div>

        {/* 留言 */}
        <div>
          <Form.Label className="fw-bold">{t('donate_message_label')}</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            placeholder={t('donate_message_placeholder')}
            value={message}
            className="user-textarea"
            onChange={(e) => setMessage(e.target.value)}
            disabled={isSubmitting}
          />
        </div>
      </Modal.Body>

      <Modal.Footer className="border-0 d-flex justify-content-end gap-2">
        <button
          className="btn btn-neutral rounded-pill"
          onClick={handleClose}
          disabled={isSubmitting}
        >
          {t('cancel')}
        </button>
        <LoadingButton
          className="btn btn-primary rounded-pill"
          onClick={handleConfirm}
          disabled={isConfirmDisabled()}
          isLoading={isSubmitting}
        >
          {t('confirm')}
        </LoadingButton>
      </Modal.Footer>
    </Modal>
  );
});

DonateModal.displayName = 'DonateModal';

export default DonateModal;
